import { useCallback, useEffect, useState } from "react";
import { FileCheck2, Loader2, Plus, ShieldCheck, ShieldX } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, Table, Td, Th } from "@/components/common/Chrome";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { EvidenceAttachments } from "@/components/common/EvidenceAttachments";
import { refOf, rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime, hashShort } from "@/lib/format";

const BLANK = {
  title: "", source_name: "", source_url: "", provision: "", jurisdiction_country: "IN",
  document_text: "", rule_keys: [],
};

export default function EvidenceRegistry() {
  const [picked, setPicked] = useState(null);
  const { can } = useAuth();
  const [items, setItems] = useState(null);
  const detail = useRowDetail();
  const [form, setForm] = useState(BLANK);
  const [keys, setKeys] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => api.get("/regulatory/evidence").then((r) => setItems(r.data)), []);
  useEffect(() => { load().catch(() => setItems([])); }, [load]);

  const save = async () => {
    setBusy("save");
    try {
      await api.post("/regulatory/evidence", {
        ...form,
        rule_keys: keys.split(",").map((k) => k.trim()).filter(Boolean),
      });
      toast.success("Evidence hashed at upload and registered");
      setOpen(false);
      setForm(BLANK);
      setKeys("");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  const verify = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/regulatory/evidence/${id}/verify`);
      toast[data.verification_status === "verified" ? "success" : "error"](
        data.verification_status === "verified"
          ? "Hash intact — evidence independently verified"
          : "Hash mismatch — tampering suspected",
      );
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  if (!items) return <Loading label="Loading evidence registry" />;
  const verified = items.filter((i) => i.verification_status === "verified").length;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-014 · Evidence Registry"
        title="Every rule stands on a hashed, verified source"
        description="Documents are SHA-256 hashed at upload. Verification recomputes the digest to prove the source extract has not changed, and promotes linked draft rules to independently evidenced."
        actions={can("evidence", "write") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-evidence-button" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="mr-1.5 h-4 w-4" /> Register evidence
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl border-border bg-card">
              <DialogHeader>
                <DialogTitle>Register a source document</DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  The extract text is hashed on save; the hash is stored for later tamper detection (NFR-SEC-008).
                </DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <F label="Title" full>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                         data-testid="evidence-title-input" className="bg-muted/40" />
                </F>
                <F label="Issuing source / authority">
                  <Input value={form.source_name} onChange={(e) => setForm({ ...form, source_name: e.target.value })}
                         data-testid="evidence-source-input" className="bg-muted/40" />
                </F>
                <F label="Jurisdiction">
                  <Input value={form.jurisdiction_country}
                         onChange={(e) => setForm({ ...form, jurisdiction_country: e.target.value.toUpperCase() })}
                         data-testid="evidence-country-input" className="bg-muted/40 font-mono" />
                </F>
                <F label="Provision / section">
                  <Input value={form.provision} onChange={(e) => setForm({ ...form, provision: e.target.value })}
                         data-testid="evidence-provision-input" className="bg-muted/40 font-mono text-xs" />
                </F>
                <F label="Source URL">
                  <Input value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })}
                         data-testid="evidence-url-input" className="bg-muted/40 text-xs" />
                </F>
                <F label="Linked rule keys (comma separated)" full>
                  <Input value={keys} onChange={(e) => setKeys(e.target.value)}
                         data-testid="evidence-rulekeys-input" className="bg-muted/40 font-mono text-xs" />
                </F>
                <F label="Source extract (hashed)" full>
                  <Textarea rows={4} value={form.document_text}
                            onChange={(e) => setForm({ ...form, document_text: e.target.value })}
                            data-testid="evidence-text-input" className="bg-muted/40 text-xs" />
                </F>
              </div>
              <Button onClick={save} disabled={busy === "save" || !form.title} data-testid="evidence-save-button"
                      className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {busy === "save" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Hash &amp; register
              </Button>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Kpi testId="kpi-evidence-total" label="Registered documents" value={items.length} accent="sky" icon={FileCheck2} />
        <Kpi testId="kpi-evidence-verified" label="Independently verified" value={verified} accent="emerald" icon={ShieldCheck}
             sub={`${items.length - verified} awaiting verification`} />
        <Kpi testId="kpi-evidence-markets" label="Markets covered" accent="violet"
             value={new Set(items.map((i) => i.jurisdiction_country)).size} />
      </div>

      <Panel testId="panel-evidence-registry" kicker="Immutable digests" title="Evidence documents">
        <Table testId="evidence-table"
               head={<><Th>Document</Th><Th>Provision</Th><Th>Market</Th><Th>SHA-256</Th><Th>Rules</Th><Th>Status</Th><Th /></>}>
          {items.length === 0 && <Empty colSpan={7} label="No evidence registered" />}
          {items.map((e) => (
            <tr key={refOf(e)} className="row-hover cursor-pointer" onClick={rowClick(() => setPicked(e))} data-testid={`evidence-row-${e.id}`}>
              <Td className="max-w-[280px]">
                <div className="truncate font-medium text-foreground">{e.title}</div>
                <div className="truncate text-[11px] text-muted-foreground">{e.source_name}</div>
              </Td>
              <Td className="font-mono text-[11px]">{e.provision || "—"}</Td>
              <Td className="font-mono text-xs text-primary">{e.jurisdiction_country}</Td>
              <Td className="font-mono text-[10px] text-muted-foreground" data-testid={`evidence-sha256-hash-cell-${e.id}`}>
                {hashShort(e.content_hash)}
              </Td>
              <Td>
                <div className="flex flex-wrap gap-1">
                  {(e.rule_keys || []).slice(0, 2).map((k) => (
                    <span key={k} className="font-mono text-[10px] text-muted-foreground">{k}</span>
                  ))}
                  {(e.rule_keys || []).length > 2 && (
                    <span className="font-mono text-[10px] text-muted-foreground/70">+{e.rule_keys.length - 2}</span>
                  )}
                </div>
              </Td>
              <Td>
                <Chip testId={`evidence-status-${e.id}`}
                      className={e.verification_status === "verified"
                        ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                        : e.verification_status === "tamper_suspected"
                          ? "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300"
                          : "border-border bg-muted text-muted-foreground"}>
                  {e.verification_status === "verified" ? <ShieldCheck className="h-3 w-3" /> : <ShieldX className="h-3 w-3" />}
                  {e.verification_status.replace(/_/g, " ")}
                </Chip>
                {e.verified_by && <div className="mt-1 text-[10px] text-muted-foreground/70">{e.verified_by} · {dateTime(e.verified_at)}</div>}
              </Td>
              <Td>
                {can("evidence", "verify") && (
                  <Button size="sm" variant="outline" onClick={() => verify(e.id)} disabled={busy === e.id}
                          data-testid={`verify-evidence-${e.id}`}
                          className="h-7 rounded-full border-border px-3 text-[11px]">
                    {busy === e.id ? <Loader2 className="h-3 w-3 animate-spin" /> : "Verify hash"}
                  </Button>
                )}
              </Td>
            </tr>
          ))}
        </Table>
      </Panel>

      <Sheet open={Boolean(picked)} onOpenChange={(v) => !v && setPicked(null)}>
        <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-xl"
                      data-testid="evidence-detail-sheet">
          {picked && (
            <>
              <SheetHeader><SheetTitle className="text-base">{picked.title}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-1.5 text-[12px]">
                {[["Issuing source", picked.source_name], ["Provision", picked.provision],
                  ["Jurisdiction", picked.jurisdiction_country],
                  ["Verification", picked.verification_status?.replace(/_/g, " ")],
                  ["Verified by", picked.verified_by ? `${picked.verified_by} · ${dateTime(picked.verified_at)}` : ""],
                  ["Linked rules", (picked.rule_keys || []).join(", ")],
                  ["Source URL", picked.source_url]].map(([k, v]) => (
                    <div key={k} className="flex items-start justify-between gap-3 border-b border-border/60 py-1.5">
                      <span className="text-[11px] text-muted-foreground">{k}</span>
                      <span className="max-w-[60%] break-words text-right text-foreground">{v || "—"}</span>
                    </div>
                ))}
              </div>
              <div className="mt-3 rounded-xl border border-border bg-muted/20 p-3 font-mono text-[10.5px] text-muted-foreground">
                sha256 {picked.content_hash}
              </div>
              {picked.document_text && (
                <div className="mt-3">
                  <div className="kicker mb-1.5">Source extract</div>
                  <p className="whitespace-pre-wrap rounded-xl border border-border bg-muted/20 p-3 text-[11.5px] text-foreground">
                    {picked.document_text}
                  </p>
                </div>
              )}
              <div className="mt-5 border-t border-border pt-4">
                <div className="kicker mb-2">Attachments</div>
                <EvidenceAttachments evidence={picked} canWrite={can("evidence", "write")}
                                     onChange={async () => {
                                       const res = await api.get("/regulatory/evidence");
                                       setItems(res.data);
                                       setPicked(res.data.find((x) => x.id === picked.id) || null);
                                     }} />
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {detail.sheet}
    </div>
  );
}

const F = ({ label, children, full }) => (
  <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}>
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);
